const API_URL = window.location.origin;

// Optimized thumbnail URL — serves resized WebP via backend cache
export const thumbUrl = (src, w = 480) => {
  if (!src) return '';
  if (src.startsWith('/api/uploads/thumbnails/')) {
    return `${API_URL}${src}?w=${w}&q=75`;
  }
  return src;
};


// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    const detail = error.detail;
    // Handle structured error objects (e.g. insufficient_balance)
    if (detail && typeof detail === 'object') {
      const err = new Error(detail.error || JSON.stringify(detail));
      err.data = detail;
      throw err;
    }
    throw new Error(detail || 'Request failed');
  }

  return response.json();
};

// Films API
export const filmsApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.genre) queryParams.append('genre', params.genre);
    if (params.content_type) queryParams.append('content_type', params.content_type);
    if (params.duration_filter) queryParams.append('duration_filter', params.duration_filter);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const query = queryParams.toString();
    return apiCall(`/films${query ? `?${query}` : ''}`);
  },

  getFeatured: () => apiCall('/films/featured'),

  getById: (filmId) => apiCall(`/films/${filmId}`),

  getCategories: () => apiCall('/films/categories'),

  getGenres: () => apiCall('/films/genres'),

  getGenresList: () => apiCall('/films/genres-list'),

  getCategoriesList: () => apiCall('/films/categories-list'),

  getTop10Weekly: () => apiCall('/films/top-10-weekly'),

  incrementView: (filmId) => apiCall(`/films/${filmId}/view`, { method: 'POST' }),

  canWatch: (filmId) => apiCall(`/films/${filmId}/can-watch`),

  getComingSoon: () => apiCall('/films/coming-soon'),

  getHomepageData: () => apiCall('/homepage'),
};

// My List API
export const myListApi = {
  getAll: () => apiCall('/my-list'),

  add: (filmId) => apiCall('/my-list', {
    method: 'POST',
    body: JSON.stringify({ film_id: filmId }),
  }),

  remove: (filmId) => apiCall(`/my-list/${filmId}`, { method: 'DELETE' }),

  check: (filmId) => apiCall(`/my-list/check/${filmId}`),
};

// Ratings API
export const ratingsApi = {
  rate: (filmId, rating) => apiCall('/ratings', {
    method: 'POST',
    body: JSON.stringify({ film_id: filmId, rating }),
  }),

  getMyRating: (filmId) => apiCall(`/ratings/${filmId}`),

  rateEpisode: (episodeId, rating) => apiCall(`/episodes/${episodeId}/rate`, {
    method: 'POST',
    body: JSON.stringify({ rating }),
  }),

  getEpisodeRatings: (seriesId) => apiCall(`/episodes/ratings?series_id=${seriesId}`),
};

// Watch Progress API
export const watchProgressApi = {
  save: (filmId, progress, totalDuration) => apiCall('/watch-progress', {
    method: 'POST',
    body: JSON.stringify({
      film_id: filmId,
      progress,
      total_duration: totalDuration,
    }),
  }),

  get: (filmId) => apiCall(`/watch-progress/${filmId}`),

  getContinueWatching: () => apiCall('/continue-watching'),
};

// User Profile API
export const userProfileApi = {
  get: () => apiCall('/user/profile'),

  update: (data) => apiCall('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/user/upload-avatar`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    
    return response.json();
  },

  changePassword: (currentPassword, newPassword) => apiCall('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  }),
};

// Profiles API (Netflix-style sub-profiles - kept for backwards compatibility)
export const profilesApi = {
  getAll: () => apiCall('/profiles'),

  create: (name, avatar) => apiCall('/profiles', {
    method: 'POST',
    body: JSON.stringify({ name, avatar }),
  }),

  delete: (profileId) => apiCall(`/profiles/${profileId}`, { method: 'DELETE' }),
};

// Subscription API
export const subscriptionApi = {
  getPlans: () => apiCall('/subscription/plans'),

  createCheckout: (plan, originUrl) => apiCall('/subscription/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan, origin_url: originUrl }),
  }),

  getStatus: (sessionId) => apiCall(`/subscription/status/${sessionId}`),

  getMySubscription: () => apiCall('/subscription/my-subscription'),

  addFamilyMember: (email) => apiCall('/subscription/add-family-member', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
};

// --- Resilient upload helpers ---

// Wait for browser to be online
const _waitForNetwork = () => new Promise((resolve) => {
  if (navigator.onLine) return resolve();
  const handler = () => { window.removeEventListener('online', handler); resolve(); };
  window.addEventListener('online', handler);
  // Also poll as fallback in case event doesn't fire
  const interval = setInterval(() => {
    if (navigator.onLine) { clearInterval(interval); window.removeEventListener('online', handler); resolve(); }
  }, 2000);
});

// Upload a single chunk with aggressive retry (never gives up while online)
const _uploadSingleChunk = async (uploadId, file, chunkIndex, chunkSize, onStatus = null, maxRetryTime = 1800000) => {
  const start = chunkIndex * chunkSize;
  const end = Math.min(start + chunkSize, file.size);
  const blob = file.slice(start, end);
  const retryStart = Date.now();
  let attempt = 0;
  let statusNotified = false;

  while (Date.now() - retryStart < maxRetryTime) {
    try {
      if (!navigator.onLine) {
        if (onStatus) onStatus('offline');
        await _waitForNetwork();
        if (onStatus) onStatus('uploading');
        statusNotified = false;
      }

      const formData = new FormData();
      formData.append('upload_id', uploadId);
      formData.append('chunk_index', chunkIndex.toString());
      formData.append('chunk', blob, `chunk_${chunkIndex}`);

      const response = await fetch(`${API_URL}/api/company/upload-video/chunk`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Chunk ${chunkIndex} failed (${response.status})`);
      }
      // Success — clear any connection issue status
      if (statusNotified && onStatus) onStatus('uploading');
      return;
    } catch (e) {
      attempt++;
      const elapsed = Date.now() - retryStart;
      // Show connection issue message only after 25 seconds of continuous failure
      if (elapsed >= 25000 && !statusNotified && onStatus) {
        onStatus('connection_issue');
        statusNotified = true;
      }
      // Exponential backoff: 2s, 4s, 8s, 16s... capped at 30s
      const delay = Math.min(2000 * Math.pow(2, attempt - 1), 30000);
      console.warn(`[Upload] Chunk ${chunkIndex} attempt ${attempt} failed: ${e.message}. Retrying in ${delay / 1000}s...`);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw new Error(`Upload failed: chunk ${chunkIndex} could not be uploaded after 30 minutes of retries`);
};

// Upload all chunks with resilient retry logic
const _uploadChunksResilient = async (uploadId, file, totalChunks, chunkSize, storageKey, onProgress, skipSet = null) => {
  let completed = skipSet ? skipSet.size : 0;

  for (let i = 0; i < totalChunks; i++) {
    if (skipSet && skipSet.has(i)) continue;

    await _uploadSingleChunk(uploadId, file, i, chunkSize, (status) => {
      // Propagate connection status to onProgress
      if (onProgress) onProgress(Math.round((completed / totalChunks) * 95), status);
    });

    completed++;
    // Update localStorage progress
    try {
      const pending = JSON.parse(localStorage.getItem(storageKey) || '{}');
      pending.last_completed_chunk = i;
      localStorage.setItem(storageKey, JSON.stringify(pending));
    } catch (_) {}

    if (onProgress) onProgress(Math.round((completed / totalChunks) * 95), 'uploading');
  }
};

// Poll for optimization/transcoding completion
const _pollForCompletion = async (uploadId, storageKey, onProgress) => {
  if (onProgress) onProgress(96);
  const POLL_INTERVAL = 5000;
  const MAX_POLL_TIME = 7200000; // 2 hours
  const pollStart = Date.now();

  while (Date.now() - pollStart < MAX_POLL_TIME) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL));
    const pollRes = await apiCall(`/company/upload-video/poll?upload_id=${uploadId}`);

    if (pollRes.status === 'completed') {
      localStorage.removeItem(storageKey);
      if (onProgress) onProgress(100);
      return pollRes;
    }
    if (pollRes.status === 'failed') {
      throw new Error(pollRes.error || 'Video processing failed');
    }
    if (pollRes.status === 'optimizing' && onProgress) {
      const step = pollRes.transcode_step || 'queued';
      const stepProgress = { queued: 96, downloading: 97, analyzing: 97, transcoding: 98, faststart: 98, uploading: 99, done: 100 };
      onProgress(stepProgress[step] || 97, step);
    }
  }
  throw new Error('Video optimization timed out. The video is still usable but may not be fully optimized.');
};

// Company API
export const companyApi = {
  register: (data) => apiCall('/company/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getMyCompany: () => apiCall('/company/my-company'),

  getContent: () => apiCall('/company/content'),

  getAgreementStatus: () => apiCall('/company/agreement-status'),

  acceptAgreement: (data = {}) => apiCall('/company/accept-agreement', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  submitContent: (data) => apiCall('/company/content/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  requestEdit: (filmId, data) => apiCall(`/company/content/${filmId}/request-edit`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  uploadContent: async (formData) => {
    const response = await fetch(`${API_URL}/api/company/content/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  },

  uploadThumbnail: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/company/upload-thumbnail`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  },

  getContentPerformance: (filmId) => apiCall(`/company/content/${filmId}/performance`),

  uploadVideo: async (file, onProgress) => {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
    const STORAGE_KEY = 'reframes_pending_upload';

    // Auto-resume: check if there's a pending upload for this exact file
    try {
      const pending = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (pending && pending.upload_id && pending.filename === file.name && pending.total_size === file.size) {
        console.log('[Upload] Found pending upload, auto-resuming:', pending.upload_id);
        if (onProgress) onProgress(1, 'resuming');
        return await companyApi.resumeUpload(pending.upload_id, file, onProgress);
      }
    } catch (_) {}

    // 1. Initialize new upload session
    const initRes = await apiCall('/company/upload-video/init', {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        total_size: file.size,
      }),
    });

    const { upload_id, total_chunks } = initRes;

    // Save to localStorage for resume capability
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      upload_id, filename: file.name, total_size: file.size,
      total_chunks, chunk_size: CHUNK_SIZE, last_completed_chunk: -1,
      created_at: new Date().toISOString(),
    }));

    // 2. Upload chunks with resilient retry
    await _uploadChunksResilient(upload_id, file, total_chunks, CHUNK_SIZE, STORAGE_KEY, onProgress);

    // 3. Complete upload — returns immediately, optimization happens in background after submit
    if (onProgress) onProgress(98, 'finalizing');
    const completeRes = await apiCall('/company/upload-video/complete', {
      method: 'POST',
      body: JSON.stringify({ upload_id }),
    });

    localStorage.removeItem(STORAGE_KEY);
    if (onProgress) onProgress(100);
    return completeRes;
  },

  resumeUpload: async (uploadId, file, onProgress) => {
    const CHUNK_SIZE = 5 * 1024 * 1024;
    const STORAGE_KEY = 'reframes_pending_upload';

    // Get server-side status to know which chunks are already uploaded
    const status = await apiCall(`/company/upload-video/status?upload_id=${uploadId}`);
    if (status.status === 'completed' || status.status === 'optimizing') {
      localStorage.removeItem(STORAGE_KEY);
      if (onProgress) onProgress(100);
      return { url: status.result_url, filename: status.result_filename, size: status.result_size || 0, duration: status.result_duration || 0 };
    }
    if (status.status !== 'in_progress') {
      localStorage.removeItem(STORAGE_KEY);
      throw new Error('Upload session is no longer active');
    }

    const uploadedSet = new Set(status.uploaded_chunks);
    const total_chunks = status.total_chunks;

    if (onProgress) onProgress(Math.round((uploadedSet.size / total_chunks) * 95));

    // Upload only missing chunks with resilient retry
    await _uploadChunksResilient(uploadId, file, total_chunks, CHUNK_SIZE, STORAGE_KEY, onProgress, uploadedSet);

    // Complete — returns immediately, optimization happens after content submission
    if (onProgress) onProgress(98, 'finalizing');
    const completeRes = await apiCall('/company/upload-video/complete', {
      method: 'POST',
      body: JSON.stringify({ upload_id: uploadId }),
    });

    localStorage.removeItem(STORAGE_KEY);
    if (onProgress) onProgress(100);
    return completeRes;
  },

  getUploadStatus: (uploadId) => apiCall(`/company/upload-video/status?upload_id=${uploadId}`),

  getPendingUpload: () => {
    try {
      return JSON.parse(localStorage.getItem('reframes_pending_upload') || 'null');
    } catch { return null; }
  },

  // Draft API
  saveDraft: (data) => apiCall('/company/drafts', { method: 'POST', body: JSON.stringify(data) }),
  getDrafts: () => apiCall('/company/drafts'),
  getDraft: (draftId) => apiCall(`/company/drafts/${draftId}`),
  deleteDraft: (draftId, cleanup = true) => apiCall(`/company/drafts/${draftId}?cleanup=${cleanup}`, { method: 'DELETE' }),

  abortUpload: (uploadId) => {
    localStorage.removeItem('reframes_pending_upload');
    return apiCall('/company/upload-video/abort', {
      method: 'POST',
      body: JSON.stringify({ upload_id: uploadId }),
    });
  },
};

// Series API
export const seriesApi = {
  getEpisodes: (seriesId) => apiCall(`/series/${seriesId}/episodes`),
  getAllEpisodes: (seriesId) => apiCall(`/series/${seriesId}/all-episodes`),
  addEpisode: (seriesId, data) => apiCall(`/series/${seriesId}/episodes`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  addSeason: (seriesId, data) => apiCall(`/series/${seriesId}/add-season`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Owner/Admin API
export const ownerApi = {
  getPendingCompanies: () => apiCall('/owner/pending-companies'),

  getAllCompanies: () => apiCall('/owner/all-companies'),

  getCompanyDetail: (companyId) => apiCall(`/owner/company/${companyId}`),

  approveCompany: (companyId, note = '') => apiCall(`/owner/company/${companyId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  }),

  rejectCompany: (companyId, reason, note = '') => apiCall(`/owner/company/${companyId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason, note }),
  }),

  suspendCompany: (companyId) => apiCall(`/owner/company/${companyId}/suspend`, {
    method: 'POST',
  }),

  deleteCompany: (companyId) => apiCall(`/owner/company/${companyId}`, {
    method: 'DELETE',
  }),

  getCompanyReviewNotes: (companyId) => apiCall(`/owner/company/${companyId}/review-notes`),

  getCreatorAgreements: (companyId) => apiCall(`/owner/creator-agreements/${companyId}`),

  downloadCreatorAgreements: (companyId) => `${API_URL}/api/owner/creator-agreements/${companyId}/download`,

  getAllAgreements: (params = {}) => {
    const q = new URLSearchParams();
    if (params.creator) q.set('creator', params.creator);
    if (params.content) q.set('content', params.content);
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.page) q.set('page', params.page);
    if (params.limit) q.set('limit', params.limit);
    return apiCall(`/admin/agreements?${q.toString()}`);
  },

  getContentReviewNotes: (filmId) => apiCall(`/owner/content/${filmId}/review-notes`),

  getPendingContent: () => apiCall('/owner/pending-content'),

  getPendingFilms: () => apiCall('/owner/pending-films'),

  getPendingSeries: () => apiCall('/owner/pending-series'),

  getContentDetail: (filmId) => apiCall(`/owner/content/${filmId}`),

  approveContent: (filmId, note = '') => apiCall(`/owner/content/${filmId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  }),

  rejectContent: (filmId, feedback, note = '') => apiCall(`/owner/content/${filmId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ feedback, note }),
  }),

  requestChanges: (filmId, message) => apiCall(`/owner/content/${filmId}/request-changes`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),

  getAllUsers: () => apiCall('/owner/all-users'),

  getAllSubscriptions: () => apiCall('/owner/all-subscriptions'),

  // Content management (approved content)
  getAllFilms: () => apiCall('/owner/all-films'),

  getAllSeries: () => apiCall('/owner/all-series'),

  getSeriesEpisodes: (seriesId) => apiCall(`/owner/series/${seriesId}/episodes`),

  deleteFilm: (filmId) => apiCall(`/owner/film/${filmId}`, {
    method: 'DELETE',
  }),

  deleteSeries: (seriesId) => apiCall(`/owner/series/${seriesId}`, {
    method: 'DELETE',
  }),

  deleteEpisode: (episodeId) => apiCall(`/owner/episode/${episodeId}`, {
    method: 'DELETE',
  }),

  deleteSubscription: (subscriptionId) => apiCall(`/owner/subscription/${subscriptionId}`, {
    method: 'DELETE',
  }),

  updateContent: (filmId, data) => apiCall(`/owner/content/${filmId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  getContentAnalytics: (filmId) => apiCall(`/owner/content/${filmId}/analytics`),

  // Platform payout settings
  getPayoutSettings: () => apiCall('/owner/payout-settings'),

  updatePayoutSettings: (settings) => apiCall('/owner/payout-settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  }),

  // Stripe API configuration
  getStripeConfig: () => Promise.resolve({ configured: true, connected: true, key_type: 'live' }),
  // Legacy endpoints removed — keys managed via environment variables

  initiateStripeConnect: (originUrl) => apiCall('/owner/connect-stripe', {
    method: 'POST',
    body: JSON.stringify({ origin_url: originUrl }),
  }),

  initiatePayoneerConnect: () => apiCall('/owner/connect-payoneer', {
    method: 'POST',
  }),

  // Creator payouts management
  getPendingPayouts: () => apiCall('/owner/pending-payouts'),

  processPayouts: () => apiCall('/owner/process-payouts', {
    method: 'POST',
  }),

  generateTestPayouts: () => apiCall('/owner/test-payout', {
    method: 'POST',
  }),

  // Ad management
  getAdConfig: () => apiCall('/ads/config'),

  updateAdConfig: (config) => apiCall('/ads/config', {
    method: 'POST',
    body: JSON.stringify(config),
  }),

  listAds: () => apiCall('/ads/list'),

  createAd: (adData) => apiCall('/ads/create', {
    method: 'POST',
    body: JSON.stringify(adData),
  }),

  deleteAd: (adId) => apiCall(`/ads/${adId}`, {
    method: 'DELETE',
  }),

  getAdStats: () => apiCall('/ads/stats'),

  // Featured content management
  getAllContent: () => apiCall('/owner/all-content'),

  getFeaturedConfig: () => apiCall('/owner/featured'),

  saveFeaturedConfig: (filmIds) => apiCall('/owner/featured', {
    method: 'POST',
    body: JSON.stringify({ film_ids: filmIds }),
  }),

  // Admin management (Owner-only)
  listAdmins: () => apiCall('/owner/admins'),

  createAdmin: (data) => apiCall('/owner/admins', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  deleteAdmin: (userId) => apiCall(`/owner/admins/${userId}`, {
    method: 'DELETE',
  }),

  // User ban management
  banUser: (userId, reason) => apiCall(`/owner/ban-user/${userId}`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),

  unbanUser: (userId) => apiCall(`/owner/unban-user/${userId}`, {
    method: 'POST',
  }),

  // Transaction history (owner-only)
  getTransactions: () => apiCall('/owner/transactions'),

  // Enhanced content analytics
  getDetailedAnalytics: (contentId) => apiCall(`/owner/content/${contentId}/detailed-analytics`),
};

// Ads API (for displaying ads)
export const adsApi = {
  getAd: (adType, filmId) => apiCall(`/ads/get/${adType}${filmId ? `?film_id=${filmId}` : ''}`),

  getConfig: () => apiCall('/ads/config'),

  trackImpression: (adId, filmId) => apiCall('/ads/track/impression', {
    method: 'POST',
    body: JSON.stringify({ ad_id: adId, film_id: filmId }),
  }),

  trackClick: (adId, filmId) => apiCall('/ads/track/click', {
    method: 'POST',
    body: JSON.stringify({ ad_id: adId, film_id: filmId }),
  }),

  trackSkip: (adId, filmId) => apiCall('/ads/track/skip', {
    method: 'POST',
    body: JSON.stringify({ ad_id: adId, film_id: filmId }),
  }),
};

// Analytics API
export const analyticsApi = {
  recordWatchTime: (filmId, seconds) => apiCall('/analytics/watch-time', {
    method: 'POST',
    body: JSON.stringify({ film_id: filmId, seconds }),
  }),

  getPlatformStats: () => apiCall('/analytics/platform-stats'),

  getCompanyAnalytics: (companyId) => apiCall(`/analytics/company/${companyId}`),

  getRevenueAnalytics: (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const qs = params.toString();
    return apiCall(`/analytics/revenue${qs ? '?' + qs : ''}`);
  },

  getRecommendations: () => apiCall('/analytics/recommendations'),
};

// Live Audience API
export const liveAudienceApi = {
  heartbeat: (filmId) => apiCall('/live/heartbeat', {
    method: 'POST',
    body: JSON.stringify({ film_id: filmId }),
  }),

  presence: (page) => apiCall('/live/presence', {
    method: 'POST',
    body: JSON.stringify({ page }),
  }),

  disconnect: (filmId) => apiCall('/live/disconnect', {
    method: 'POST',
    body: JSON.stringify({ film_id: filmId }),
  }),

  getAudience: () => apiCall('/live/audience'),
};

// Donations API
export const donationsApi = {
  send: (creatorId, amount, message, filmId) => apiCall('/donations/send', {
    method: 'POST',
    body: JSON.stringify({ creator_id: creatorId, amount, message, film_id: filmId }),
  }),

  getReceived: () => apiCall('/donations/received'),

  getCreatorInfo: (creatorId) => apiCall(`/donations/for-creator/${creatorId}`),

  requestCashout: () => apiCall('/donations/cashout', { method: 'POST' }),
};

// Creator Earnings API
export const creatorEarningsApi = {
  getEarnings: () => apiCall('/creator/earnings'),

  updatePaymentSetup: (data) => apiCall('/creator/payment-setup', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getRevenueDetails: () => apiCall('/creator/revenue-details'),
};

export const creatorAudienceApi = {
  getAudience: (period = 'week', startDate, endDate) => {
    const params = new URLSearchParams({ period });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return apiCall(`/creator/audience?${params.toString()}`);
  },
};

// Premium Content (Pay-Per-View) API
export const premiumContentApi = {
  // Check if creator is eligible to sell premium content
  checkEligibility: () => apiCall('/premium/eligibility'),

  // Set or unset content as premium
  setContent: (filmId, isPremium, price) => apiCall('/premium/set-content', {
    method: 'POST',
    body: JSON.stringify({ film_id: filmId, is_premium: isPremium, price }),
  }),

  // Create checkout session for premium content purchase
  createPurchase: (filmId, originUrl) => apiCall(`/premium/purchase/${filmId}`, {
    method: 'POST',
    body: JSON.stringify({ origin_url: originUrl }),
  }),

  // Check purchase status
  getPurchaseStatus: (sessionId) => apiCall(`/premium/purchase-status/${sessionId}`),

  // Get user's purchased content
  getMyPurchases: () => apiCall('/premium/my-purchases'),

  // Check if user has access to premium content
  checkAccess: (filmId) => apiCall(`/premium/check-access/${filmId}`),

  // Get creator's premium content earnings
  getCreatorEarnings: () => apiCall('/premium/creator-earnings'),

  // Set batch of films + episodes as premium
  setBatch: (filmIds, episodeIds, isPremium, price) => apiCall('/premium/set-batch', {
    method: 'POST',
    body: JSON.stringify({ film_ids: filmIds, episode_ids: episodeIds, is_premium: isPremium, price }),
  }),

  // Set episodes as premium
  setEpisodes: (episodeIds, isPremium, price) => apiCall('/premium/set-episodes', {
    method: 'POST',
    body: JSON.stringify({ episode_ids: episodeIds, is_premium: isPremium, price }),
  }),
};

// Creator Shop API
export const shopApi = {
  // Creator product management
  getMyProducts: () => apiCall('/shop/creator/products'),

  createProduct: (productData) => apiCall('/shop/creator/products', {
    method: 'POST',
    body: JSON.stringify(productData),
  }),

  updateProduct: (productId, productData) => apiCall(`/shop/creator/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  }),

  deleteProduct: (productId) => apiCall(`/shop/creator/products/${productId}`, {
    method: 'DELETE',
  }),

  // Upload product image
  uploadProductImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/shop/creator/upload-image`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    
    return response.json();
  },

  // Public storefront
  getStorefront: (companyId) => apiCall(`/shop/creator/${companyId}`),

  // Purchase
  createPurchase: (productId, quantity, originUrl) => apiCall(`/shop/purchase/${productId}`, {
    method: 'POST',
    body: JSON.stringify({ quantity, origin_url: originUrl }),
  }),

  getPurchaseStatus: (sessionId) => apiCall(`/shop/purchase/status/${sessionId}`),

  // Product detail
  getProduct: (productId) => apiCall(`/shop/product/${productId}`),

  // Cart
  getCart: () => apiCall('/shop/cart'),
  addToCart: (productId, quantity, selectedVariants) => apiCall('/shop/cart/add', {
    method: 'POST',
    body: JSON.stringify({ product_id: productId, quantity, selected_variants: selectedVariants }),
  }),
  updateCartItem: (index, quantity) => apiCall('/shop/cart/update', {
    method: 'POST',
    body: JSON.stringify({ index, quantity }),
  }),
  removeFromCart: (index) => apiCall(`/shop/cart/remove/${index}`, { method: 'DELETE' }),

  // Checkout
  checkout: (shipping, originUrl, paymentMethod = 'card') => apiCall('/shop/checkout', {
    method: 'POST',
    body: JSON.stringify({ shipping, origin_url: originUrl, payment_method: paymentMethod }),
  }),

  // Creator orders
  getCreatorOrders: () => apiCall('/shop/creator/orders'),
  getCreatorOrder: (orderId) => apiCall(`/shop/creator/orders/${orderId}`),
  fulfillOrder: (orderId, trackingCode, originUrl) => apiCall(`/shop/creator/orders/${orderId}/fulfill`, {
    method: 'POST',
    body: JSON.stringify({ tracking_code: trackingCode, origin_url: originUrl }),
  }),
  updateTracking: (orderId, trackingCode, originUrl) => apiCall(`/shop/creator/orders/${orderId}/tracking`, {
    method: 'POST',
    body: JSON.stringify({ tracking_code: trackingCode, origin_url: originUrl }),
  }),

  // User orders
  getMyOrders: () => apiCall('/shop/my-orders'),
  getMyPurchases: () => apiCall('/shop/my-purchases'),
};

// Unified Creator Earnings API
export const unifiedEarningsApi = {
  getEarnings: () => apiCall('/creator/unified-earnings'),

  getWallet: () => apiCall('/creator/wallet'),
};

// Unified Financials API
export const financialsApi = {
  get: (period = 'monthly', startDate, endDate) => {
    const params = new URLSearchParams({ period });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return apiCall(`/creator/financials?${params.toString()}`);
  },
  getPayoutMethods: () => apiCall('/creator/payout-methods'),
  updatePayoutMethods: (data) => apiCall('/creator/payout-methods', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Creator Payout API
export const creatorPayoutApi = {
  // Payout methods
  getPayoutMethods: () => apiCall('/creator/payout-methods'),

  updatePayoutMethod: (methodData) => apiCall('/creator/payout-methods', {
    method: 'POST',
    body: JSON.stringify(methodData),
  }),

  setPrimaryMethod: (method) => apiCall('/creator/payout-methods/set-primary', {
    method: 'POST',
    body: JSON.stringify({ method }),
  }),

  // Connect accounts - Stripe Connect Express
  initiateStripeConnect: (originUrl) => apiCall('/creator/connect-stripe', {
    method: 'POST',
    body: JSON.stringify({ return_url: originUrl }),
  }),

  // Check Stripe account status
  getStripeAccountStatus: () => apiCall('/creator/stripe-account-status'),

  getStripeDashboardLink: () => apiCall('/creator/stripe-dashboard-link'),

  disconnectStripe: (password) => apiCall('/creator/disconnect-stripe', {
    method: 'POST',
    body: JSON.stringify({ password }),
  }),

  replaceStripe: (password, returnUrl) => apiCall('/creator/replace-stripe', {
    method: 'POST',
    body: JSON.stringify({ password, return_url: returnUrl }),
  }),

  initiatePayoneerConnect: () => apiCall('/creator/connect-payoneer', {
    method: 'POST',
  }),

  // Withdrawals
  getPayoutHistory: () => apiCall('/creator/payout-history'),

  requestPayout: (amount, method) => apiCall('/creator/request-payout', {
    method: 'POST',
    body: JSON.stringify({ amount, method }),
  }),
};

// Admin API (legacy)
export const adminApi = {
  createFilm: (filmData) => apiCall('/admin/films', {
    method: 'POST',
    body: JSON.stringify(filmData),
  }),

  updateFilm: (filmId, filmData) => apiCall(`/admin/films/${filmId}`, {
    method: 'PUT',
    body: JSON.stringify(filmData),
  }),

  deleteFilm: (filmId) => apiCall(`/admin/films/${filmId}`, { method: 'DELETE' }),

  seedDatabase: () => apiCall('/admin/seed', { method: 'POST' }),
};

// User Payment Methods API
export const paymentMethodsApi = {
  getAll: () => apiCall('/user/payment-methods'),

  add: (cardData) => apiCall('/user/payment-methods', {
    method: 'POST',
    body: JSON.stringify(cardData),
  }),

  remove: (paymentMethodId) => apiCall(`/user/payment-methods/${paymentMethodId}`, {
    method: 'DELETE',
  }),

  setDefault: (paymentMethodId) => apiCall(`/user/payment-methods/${paymentMethodId}/set-default`, {
    method: 'POST',
  }),
};

// Enhanced Donations API (with payment methods)
export const donationsApiV2 = {
  sendWithPayment: (creatorId, amount, message, paymentMethodId, newCard, saveCard, filmId) => 
    apiCall('/donations/send-with-payment', {
      method: 'POST',
      body: JSON.stringify({
        creator_id: creatorId,
        amount,
        message,
        payment_method_id: paymentMethodId,
        new_card: newCard,
        save_card: saveCard,
        film_id: filmId,
      }),
    }),
};

// Enhanced PPV API (with payment methods)
export const ppvApiV2 = {
  purchaseWithPayment: (filmId, paymentMethodId, newCard, saveCard) =>
    apiCall(`/premium/purchase-with-payment/${filmId}`, {
      method: 'POST',
      body: JSON.stringify({
        payment_method_id: paymentMethodId,
        new_card: newCard,
        save_card: saveCard,
      }),
    }),
};

// Default avatar for new users
export const DEFAULT_AVATAR = 'https://static.prod-images.emergentagent.com/jobs/2506b7c0-1d90-43d8-9210-036408a9bd73/images/97e243c8b7c7ba2a59f817da153736c271279faa1190b8fbc8f6e42e6d0783c0.png';

// Avatar options for profile customization
export const avatarOptions = [
  DEFAULT_AVATAR,
];

// Format duration (seconds to mm:ss or hh:mm:ss)
export const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '';
  const s = Math.round(Number(seconds) || 0);
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};


// ==================== CONSENT & LEGAL API ====================
export const consentApi = {
  geolocate: () => apiCall('/consent/geolocate'),
  record: (data) => apiCall('/consent/record', { method: 'POST', body: JSON.stringify(data) }),
};

export const legalApi = {
  getFooterLinks: () => apiCall('/legal/footer'),
  getPages: () => apiCall('/legal/pages'),
  getPage: (slug) => apiCall(`/legal/pages/${slug}`),
  getRequiredPolicies: () => apiCall('/legal/required-policies'),
  // Policy consent
  acceptPolicies: (data) => apiCall('/auth/accept-policies', { method: 'POST', body: JSON.stringify(data) }),
  recordRegistrationConsent: (data) => apiCall('/auth/record-registration-consent', { method: 'POST', body: JSON.stringify(data) }),
  getPendingPolicies: () => apiCall('/auth/pending-policies'),
  getUserPolicyConsents: (userId) => apiCall(`/owner/users/${userId}/policy-consents`),
  // Owner
  ownerGetPages: () => apiCall('/owner/legal-pages'),
  ownerUpdatePage: (pageId, data) => apiCall(`/owner/legal-pages/${pageId}`, { method: 'PUT', body: JSON.stringify(data) }),
  ownerCreatePage: (data) => apiCall('/owner/legal-pages', { method: 'POST', body: JSON.stringify(data) }),
  ownerDeletePage: (pageId) => apiCall(`/owner/legal-pages/${pageId}`, { method: 'DELETE' }),
  // Owner zones
  ownerGetZones: () => apiCall('/owner/consent-zones'),
  ownerUpdateZone: (zoneId, data) => apiCall(`/owner/consent-zones/${zoneId}`, { method: 'PUT', body: JSON.stringify(data) }),
  ownerCreateZone: (data) => apiCall('/owner/consent-zones', { method: 'POST', body: JSON.stringify(data) }),
  ownerDeleteZone: (zoneId) => apiCall(`/owner/consent-zones/${zoneId}`, { method: 'DELETE' }),
};

export const mostWatchedApi = {
  get: () => apiCall('/films/most-watched'),
};

export const creatorsApi = {
  search: (q) => apiCall(`/films/creators/search?q=${encodeURIComponent(q)}`),
  getFilms: (creatorName) => apiCall(`/films/creators/${encodeURIComponent(creatorName)}/films`),
};

export const paymentsApi = {
  createCheckout: (data) => apiCall('/payments/checkout', { method: 'POST', body: JSON.stringify(data) }),
  getStatus: (sessionId) => apiCall(`/payments/status/${sessionId}`),
  setupCard: (originUrl) => apiCall('/payments/setup-card', { method: 'POST', body: JSON.stringify({ origin_url: originUrl }) }),
  checkSetupStatus: (sessionId) => apiCall(`/payments/setup-card/status/${sessionId}`),
  getSavedCard: () => apiCall('/payments/saved-card'),
  removeCard: () => apiCall('/payments/saved-card', { method: 'DELETE' }),
  replaceCard: (originUrl) => apiCall('/payments/replace-card', { method: 'POST', body: JSON.stringify({ origin_url: originUrl }) }),
  topupBalance: (pkg, originUrl) => apiCall('/payments/topup-balance', { method: 'POST', body: JSON.stringify({ package: pkg, origin_url: originUrl }) }),
  subscribe: (plan, originUrl) => apiCall('/payments/subscribe', { method: 'POST', body: JSON.stringify({ plan, origin_url: originUrl }) }),
  getBalance: () => apiCall('/payments/user-balance'),
  getTransactions: () => apiCall('/payments/user-transactions'),
  getCreatorRevenue: () => apiCall('/payments/creator-revenue'),
  getPlatformRevenue: () => apiCall('/payments/platform-revenue'),
  processPayment: (data) => apiCall('/payments/process-payment', { method: 'POST', body: JSON.stringify(data) }),
  getPaymentInfo: () => apiCall('/payments/payment-info'),
  getSubscriptionStatus: () => apiCall('/payments/subscription-status'),
  cancelSubscription: () => apiCall('/payments/cancel-subscription', { method: 'POST' }),
  confirmSubscription: (sessionId) => apiCall('/payments/confirm-subscription', { method: 'POST', body: JSON.stringify({ session_id: sessionId }) }),
  stripeHealth: () => apiCall('/payments/stripe-health'),
  fixCustomer: () => apiCall('/payments/fix-customer', { method: 'POST' }),
};

export const editRequestsApi = {
  submitEpisodeEdit: (episodeId, data) => apiCall(`/company/episodes/${episodeId}/edit-request`, { method: 'POST', body: JSON.stringify(data) }),
  updateAndResubmitEpisode: (episodeId, data) => apiCall(`/company/episodes/${episodeId}/update-and-resubmit`, { method: 'PUT', body: JSON.stringify(data) }),
  submitSettingsEdit: (data) => apiCall('/company/settings/edit-request', { method: 'POST', body: JSON.stringify(data) }),
  getMyRequests: () => apiCall('/company/edit-requests'),
  getAllRequests: () => apiCall('/owner/edit-requests'),
  approve: (requestId, reason) => apiCall(`/owner/edit-requests/${requestId}/approve`, { method: 'POST', body: JSON.stringify({ reason }) }),
  reject: (requestId, reason) => apiCall(`/owner/edit-requests/${requestId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  getRatingDistribution: (period) => apiCall(`/company/rating-distribution?period=${period}`),
};

export const reportsApi = {
  report: (filmId, reason) => apiCall(`/films/${filmId}/report`, { method: 'POST', body: JSON.stringify({ reason }) }),
  getAll: (status = 'all') => apiCall(`/owner/reports?status=${status}`),
  resolve: (reportId, action, note = '') => apiCall(`/owner/reports/${reportId}/resolve`, { method: 'POST', body: JSON.stringify({ action, note }) }),
};

export const notificationsApi = {
  getAll: (limit = 50) => apiCall(`/notifications?limit=${limit}`),
  markRead: (ids) => apiCall('/notifications/mark-read', { method: 'POST', body: JSON.stringify({ notification_ids: ids }) }),
  markAllRead: () => apiCall('/notifications/mark-all-read', { method: 'POST' }),
};

export const contentApi = {
  resubmit: (filmId) => apiCall(`/company/content/${filmId}/resubmit`, { method: 'POST' }),
  updateAndResubmit: (filmId, data) => apiCall(`/company/content/${filmId}/update-and-resubmit`, { method: 'PUT', body: JSON.stringify(data) }),
};

export const relatedFilmsApi = {
  get: (filmId) => apiCall(`/films/${filmId}/related`),
};

export const episodesApi = {
  incrementView: (episodeId) => apiCall(`/episodes/${episodeId}/view`, { method: 'POST' }),
};

export const creatorChartsApi = {
  getViewsOverTime: (period = '30d') => apiCall(`/creator/views-over-time?period=${period}`),
};

